import { ContractTemplateFieldName } from '@/core/collections/contracts-api/contract-template-field-names/contract-template-field-name.interface';
import { ContractTemplateDTO } from '@/core/collections/contracts-api/customers/contract-templates/contract-template.interface';
import { normalizeYupErrorPath } from '@nxtpeople/nxt-styles';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';
import { ValidationError } from 'yup';

const BASIC_TAB_FIELD_KEYS = [
   'team_id',
   'invoice_group_id',
   'cost_center_id',
   'department_id',
   'fulltime_week_id',
   'shift_id',
   'employment_type',
   'worker_statute_id',
   'worker_type_id',
];

const DETAIL_TAB_FIELD_KEYS = [
   'employment_street',
   'employment_house_number',
   'employment_box',
   'employment_city_id',
   'job_function_id',
   'employment_motive_id',
   'replacement_motive_id',
   'salary',
   'salary_type_id',
   'salary_include_id',
   'comment',
];

const PERFORMANCE_TAB_FIELD_KEYS = ['recruitment_type_id', 'activity_risk_id', 'badge_number', 'shift_type_id', 'invoice_comment'];

function getTemplateFieldValue(template: ContractTemplateDTO, fieldKey: string) {
   return template.contract_template_fields?.find((field) => field.field_key === fieldKey)?.value;
}

function isEmptyValue(value: unknown) {
   return value === null || value === undefined || value === '';
}

function appendRequiredTemplateFieldValidation(
   schema: yup.ObjectSchema<any>,
   tabFieldKeys: Array<string>,
   getFieldNames: () => Array<ContractTemplateFieldName>,
   requiredMessage: string,
) {
   return schema.test('required-template-fields', '', function validateRequiredTemplateFields(template: ContractTemplateDTO) {
      const innerErrors: ValidationError[] = [];

      for (const fieldName of getFieldNames()) {
         if (!fieldName.is_required || !tabFieldKeys.includes(fieldName.key)) continue;

         const value = getTemplateFieldValue(template, fieldName.key);
         if (isEmptyValue(value)) {
            innerErrors.push(new ValidationError(requiredMessage, value, fieldName.key));
         }
      }

      if (innerErrors.length) {
         throw new ValidationError(innerErrors);
      }

      return true;
   });
}

export function useContractTemplateWizardSchema(getFieldNames: () => Array<ContractTemplateFieldName>) {
   const { t } = useI18n();
   const requiredMessage = t('generic.feedback.required');

   const BASIC_DATA_SCHEMA = appendRequiredTemplateFieldValidation(
      yup.object({
         name: yup.string().required().label(t('generic.name')),
         employer_id: yup.number().required().label(t('contracts.employer', 1)),
         employer_company_joint_committee_id: yup.number().required().label(t('contracts.employer-joint-commitee')),
         user_company_joint_committee_id: yup.number().required().label(t('contracts.company-joint-commitee')),
      }),
      BASIC_TAB_FIELD_KEYS,
      getFieldNames,
      requiredMessage,
   );

   const DETAIL_DATA_SCHEMA = appendRequiredTemplateFieldValidation(yup.object({}), DETAIL_TAB_FIELD_KEYS, getFieldNames, requiredMessage);

   const PERFORMANCE_DATA_SCHEMA = appendRequiredTemplateFieldValidation(
      yup.object({
         default_commercial_agreement_id: yup.number().required().label(t('contracts.commercial-agreement')),
      }),
      PERFORMANCE_TAB_FIELD_KEYS,
      getFieldNames,
      requiredMessage,
   );

   function getSchemaForTab(tabKey: string) {
      if (tabKey === 'basic-data') return BASIC_DATA_SCHEMA;
      if (tabKey === 'detail-data') return DETAIL_DATA_SCHEMA;
      if (tabKey === 'performance-data') return PERFORMANCE_DATA_SCHEMA;
      return BASIC_DATA_SCHEMA;
   }

   return { BASIC_DATA_SCHEMA, DETAIL_DATA_SCHEMA, PERFORMANCE_DATA_SCHEMA, getSchemaForTab };
}

export function mapContractTemplateWizardErrors(
   errors: Record<string, string>,
   template: ContractTemplateDTO,
   fieldNames: Array<ContractTemplateFieldName>,
) {
   const mapped: Record<string, string> = {};

   for (const [path, message] of Object.entries(errors)) {
      const normalizedPath = normalizeYupErrorPath(path);
      const fieldMatch = normalizedPath.match(/^contract_template_fields\[(\d+)\]\.(\w+)$/);

      if (fieldMatch) {
         const field = template.contract_template_fields?.[Number(fieldMatch[1])];
         const fieldKey = field?.field_key || fieldNames.find((fieldName) => fieldName.id === field?.contract_template_field_name_id)?.key;
         if (fieldKey) {
            mapped[fieldKey] = message;
            continue;
         }
      }

      mapped[path] = message;
   }

   if (mapped.user_company_joint_committee_id) {
      mapped.employer_company_joint_committee_id = mapped.employer_company_joint_committee_id || mapped.user_company_joint_committee_id;
   }

   if (mapped.fulltime_week_id && !mapped.fulltime_id) {
      mapped.fulltime_id = mapped.fulltime_week_id;
   }

   return mapped;
}
